import { memo } from 'react';

import { HandMetal } from '../../icons/lucide/HandMetal';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { counterTint } from '../../utils/color';
import { Avatar } from '../Avatar';
import { CardChipTooltip } from '../cards/CardChipTooltip';
import { FolderLikeCard } from '../cards/FolderLikeCard';
import type { StudyGroupCardProps, StudyGroupMember } from './StudyGroupCard.types';

/** The design's sample study-group red. */
const DEFAULT_GROUP_COLOR = '#fb2c36';
/** Max faces in the pile; the rest collapse into a "+N" counter. */
const MAX_FACES = 2;

/**
 * Overlapping member facepile — reuses the shared Avatar. The 2px ring
 * matches whatever the faces sit ON: the GROUP's identity colour on the grid
 * tile's colour folder (inline style — arbitrary runtime hex), the card
 * surface (`secondary-50`, auto-inverting white↔near-black) on list rows.
 */
function AvatarStack({
  members,
  color,
  ring = 'color',
}: {
  members: StudyGroupMember[];
  color: string;
  ring?: 'color' | 'surface';
}) {
  const ringStyle = ring === 'color' ? ({ borderColor: color } as const) : undefined;
  const faces = members.slice(0, MAX_FACES);
  const surplus = members.length - faces.length;
  // Group-palette tint pair for the overflow counter — the shared recipe the
  // group header's `MemberAvatarStack` reads too.
  const tint = counterTint(color);
  const counterStyle = { backgroundColor: tint.backgroundColor, ...ringStyle };
  const counterTextStyle = { color: tint.color };
  return (
    <View className="flex-row items-center">
      {faces.map((m, i) => (
        // `tooltip` (web hover only) carries whatever the caller wants to
        // reveal about the member — the app passes the handle + last-activity
        // card the legacy tile had.
        <CardChipTooltip key={`${m.name}-${i}`} label={m.tooltip}>
          <Avatar
            size="xs"
            name={m.name}
            src={m.src}
            className={cn(
              'border-2',
              ring === 'surface' && 'border-secondary-50',
              i > 0 && '-ml-2',
            )}
            webProps={ringStyle && { style: ringStyle }}
            nativeProps={ringStyle && { style: ringStyle }}
          />
        </CardChipTooltip>
      ))}
      {surplus > 0 ? (
        // Overflow counter — one more "face" carrying the hidden-member
        // count, IN the group's palette: a light white-mix of the group
        // colour under text darkened from the same colour (the deck-preview
        // tint family — inline styles, arbitrary runtime hex,
        // mode-invariant). Same 28px footprint AND the same 2px ring as the
        // avatars: group colour on the grid tile, surface on list rows.
        <View
          className={cn(
            '-ml-2 h-7 min-w-7 flex-row items-center justify-center rounded-full border-2 px-1.5',
            ring === 'surface' && 'border-secondary-50',
          )}
          webProps={{ style: counterStyle }}
          nativeProps={{ style: counterStyle }}>
          <Text
            className="text-xs font-bold !leading-[normal] native:font-mona-bold"
            webProps={{ style: counterTextStyle }}
            nativeProps={{ style: counterTextStyle }}
            selectable={false}>
            {`+${surplus}`}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

/**
 * Cross-platform StudyGroupCard — the DS card for a study group. Renders
 * identically on web and React Native. See {@link StudyGroupCardProps}.
 *
 * @example
 * ```tsx
 * import { StudyGroupCard } from '@flashka/shared/ui';
 *
 * <StudyGroupCard
 *   title="Anatomy crew"
 *   color="#fb2c36"
 *   members={[{ name: 'Ada', src }, { name: 'Bruno' }]}
 *   badges={[
 *     { count: 5, icon: UsersRound, label: t('group.members', { count: 5 }) },
 *     { count: 6, icon: Layers, label: t('group.decks', { count: 6 }) },
 *   ]}
 *   onPress={openGroup}
 * />
 * ```
 */
function StudyGroupCardImpl({
  color = DEFAULT_GROUP_COLOR,
  members,
  icon,
  ...props
}: StudyGroupCardProps) {
  const hasMembers = !!members?.length;
  // The group's own icon (emoji / curated) when the caller has one; the
  // colour-tinted hand-metal glyph is only the fallback. BOTH grid panels
  // put the glyph inline on the title row at 16px (compact 5729:10364,
  // expanded 5729:10457); list rows stay 20.
  const isGrid = (props.view ?? 'grid') === 'grid';
  const glyph = icon ?? <HandMetal color={color} size={isGrid ? 16 : 20} />;
  return (
    <FolderLikeCard
      {...props}
      color={color}
      tallGridPanel
      listGlyph={glyph}
      gridGlyphRow={glyph}
      gridTileOverlay={
        hasMembers ? (
          <View
            className={cn(
              'absolute',
              (props.size ?? 'compact') === 'expanded'
                ? 'left-3.5 top-3.5'
                : 'left-2.5 top-2.5',
            )}>
            <AvatarStack members={members!} color={color} />
          </View>
        ) : undefined
      }
      listChipsLeading={
        hasMembers ? (
          <AvatarStack members={members!} color={color} ring="surface" />
        ) : undefined
      }
    />
  );
}
/**
 * `memo`, like `DeckCard` / `FolderCard`: the tile above re-renders on store
 * ticks it cannot avoid; the card must not follow. Slot props (`icon`,
 * `members`, `previews`, `badges`, handlers) have to be memoised at the OWNER
 * or the memo silently never holds.
 */
export const StudyGroupCard = memo(StudyGroupCardImpl);
(StudyGroupCard as { displayName?: string }).displayName = 'StudyGroupCard';
